import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format, startOfMonth, startOfWeek, startOfYear, subDays, subMonths } from "date-fns";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Pill, Download, FileText, ReceiptText, RefreshCw,
  Search, Filter, ChevronDown, ChevronUp, X, PieChart as PieIcon, BarChart3, List, MapPin,
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";

import { API, STATUS_CONFIG, formatDateIST, formatMode } from "./pharmacy/pharmacyConstants";
import { PharmacyKPIs } from "./pharmacy/PharmacyKPIs";
import { PharmacyCharts } from "./pharmacy/PharmacyCharts";
import { PharmacyOrdersTable } from "./pharmacy/PharmacyOrdersTable";
import { PharmacyDetailedTable } from "./pharmacy/PharmacyDetailedTable";
import { PharmacyDrillDown } from "./pharmacy/PharmacyDrillDown";
import { PatientBillModal } from "./pharmacy/PatientBillModal";
import { GstReportModal } from "./pharmacy/GstReportModal";
import ScheduleReport from "./pharmacy/ScheduleReport";

const DATE_PRESETS = [
  { label: "Today", fn: () => { const t = new Date(); return [t, t]; } },
  { label: "Yesterday", fn: () => { const t = subDays(new Date(), 1); return [t, t]; } },
  { label: "This Week", fn: () => [startOfWeek(new Date(), { weekStartsOn: 1 }), new Date()] },
  { label: "This Month", fn: () => [startOfMonth(new Date()), new Date()] },
  { label: "Last 3 Months", fn: () => [subMonths(new Date(), 3), new Date()] },
  { label: "Last 6 Months", fn: () => [subMonths(new Date(), 6), new Date()] },
  { label: "This Year", fn: () => [startOfYear(new Date()), new Date()] },
];

const EMPTY_FILTERS = { patient_types: "", statuses: "", mrn: "", patient_name: "", doctor: "", scheme: "", billable: "", created_by: "", patient_type_number: "", search: "", payment_mode: "", medicine_name: "" };

export default function PharmacyReport() {
  const navigate = useNavigate();
  const [dateFrom, setDateFrom] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [dateTo, setDateTo] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [datePreset, setDatePreset] = useState("Today");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ patient_types: [], doctors: [], schemes: [], statuses: [], creators: [], payment_modes: [] });

  const [summary, setSummary] = useState(null);
  const [charts, setCharts] = useState(null);
  const [orders, setOrders] = useState({ data: [], total: 0, page: 1, page_size: 20, total_pages: 0 });
  const [loading, setLoading] = useState(true);

  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);

  const [drillOrder, setDrillOrder] = useState(null);
  const [chartView, setChartView] = useState("trend");
  const [showPatientBill, setShowPatientBill] = useState(false);
  const [patientBillForm, setPatientBillForm] = useState({ mrn: "", visit_no: "", patient_name: "", ward_room_bed: "", admitted_time: "", consultant: "", discharged: "" });
  const [patientBillLoading, setPatientBillLoading] = useState(false);
  const [showGstReport, setShowGstReport] = useState(false);
  const [gstDateFrom, setGstDateFrom] = useState(format(new Date(), "yyyy-MM-dd"));
  const [gstDateTo, setGstDateTo] = useState(format(new Date(), "yyyy-MM-dd"));
  const [gstData, setGstData] = useState(null);
  const [gstLoading, setGstLoading] = useState(false);
  const [showPdfMenu, setShowPdfMenu] = useState(false);
  const [detailedView, setDetailedView] = useState(false);
  const [showScheduleReport, setShowScheduleReport] = useState(false);

  const toggleScheduleReport = () => {
    setShowScheduleReport(v => {
      if (!v) {
        // Switch to This Month when opening schedule report
        const now = new Date();
        setDateFrom(format(startOfMonth(now), "yyyy-MM-dd"));
        setDateTo(format(now, "yyyy-MM-dd"));
        setDatePreset("This Month");
      }
      return !v;
    });
  };

  const buildParams = useCallback(() => {
    const p = { date_from: dateFrom, date_to: dateTo };
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [dateFrom, dateTo, filters]);

  useEffect(() => {
    axios.get(`${API}/pharmacy/filter-options`).then(r => setFilterOptions(r.data)).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams();
      const orderParams = { ...params, sort_by: sortBy, sort_order: sortOrder, page, page_size: detailedView ? 50 : 20 };
      if (detailedView) orderParams.detailed = true;
      const [sumRes, chartRes, ordRes] = await Promise.all([
        axios.get(`${API}/pharmacy/summary`, { params }),
        axios.get(`${API}/pharmacy/charts`, { params }),
        axios.get(`${API}/pharmacy/orders`, { params: orderParams }),
      ]);
      setSummary(sumRes.data);
      setCharts(chartRes.data);
      setOrders(ordRes.data);
    } catch { toast.error("Failed to load pharmacy report"); }
    setLoading(false);
  }, [buildParams, sortBy, sortOrder, page, detailedView]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePreset = (p) => { setDatePreset(p.label); const [from, to] = p.fn(); setDateFrom(format(from, "yyyy-MM-dd")); setDateTo(format(to, "yyyy-MM-dd")); setPage(1); };
  const handleSort = (col) => { if (sortBy === col) setSortOrder(o => o === "asc" ? "desc" : "asc"); else { setSortBy(col); setSortOrder("desc"); } setPage(1); };

  const openDrillDown = async (orderId) => {
    try {
      const r = await axios.get(`${API}/pharmacy/drill-down/${orderId}`);
      setDrillOrder(r.data);
    } catch { toast.error("Failed to load order details"); }
  };

  const handleExport = async (fmt) => {
    try {
      const params = { ...buildParams(), format: fmt };
      if (detailedView) params.detailed = true;
      const r = await axios.get(`${API}/pharmacy/export`, { params, responseType: fmt === "csv" ? "blob" : "json" });
      if (fmt === "csv") {
        const url = window.URL.createObjectURL(new Blob([r.data]));
        const a = document.createElement("a"); a.href = url; a.download = detailedView ? "pharmacy_detailed_report.csv" : "pharmacy_report.csv"; a.click();
      }
      toast.success(`Exported as ${fmt.toUpperCase()}`);
    } catch { toast.error("Export failed"); }
  };

  const handlePdfExport = async (reportType) => {
    try {
      toast.info("Generating PDF report...");
      const params = { ...buildParams(), report_type: reportType };
      const r = await axios.get(`${API}/pharmacy/export-pdf`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `pharmacy_report_${reportType}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded successfully");
    } catch { toast.error("PDF export failed"); }
  };

  const handleCreditBill = async (orderId) => {
    try {
      toast.info("Generating Credit Bill...");
      const r = await axios.get(`${API}/pharmacy/credit-bill/${orderId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `credit_bill_${orderId}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Credit Bill downloaded");
    } catch { toast.error("Credit Bill generation failed"); }
  };

  const handlePatientBill = async () => {
    if (!patientBillForm.mrn) { toast.error("MRN is required"); return; }
    setPatientBillLoading(true);
    try {
      toast.info("Generating Patient Bill...");
      const payload = {
        ...patientBillForm,
        date_from: dateFrom,
        date_to: dateTo,
        patient_types: filters.patient_types || "",
        patient_type_number: filters.patient_type_number || "",
        statuses: filters.statuses || "",
      };
      // Step 1: Validate — check if orders exist (JSON response, no blob issues)
      const check = await axios.post(`${API}/pharmacy/patient-bill`, { ...payload, validate_only: true });
      if (!check.data?.ok) {
        toast.error(check.data?.detail || "No orders found for this MRN/IP number");
        setPatientBillLoading(false);
        return;
      }
      // Step 2: Generate PDF (orders confirmed to exist)
      const r = await axios.post(`${API}/pharmacy/patient-bill`, payload, { responseType: "arraybuffer" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `patient_bill_${patientBillForm.mrn}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Patient Bill downloaded");
      setShowPatientBill(false);
    } catch (err) {
      const msg = err.response?.data?.detail || "Patient Bill generation failed";
      toast.error(msg);
    }
    setPatientBillLoading(false);
  };

  const fetchGstData = async () => {
    setGstLoading(true);
    try {
      const r = await axios.get(`${API}/pharmacy/gst-report`, { params: { date_from: gstDateFrom, date_to: gstDateTo } });
      setGstData(r.data);
    } catch { toast.error("Failed to load GST data"); }
    setGstLoading(false);
  };

  const downloadGstPdf = async () => {
    try {
      toast.info("Generating GST PDF...");
      const r = await axios.get(`${API}/pharmacy/gst-report/pdf`, { params: { date_from: gstDateFrom, date_to: gstDateTo }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `gst_report_${gstDateFrom}_${gstDateTo}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("GST PDF downloaded");
    } catch { toast.error("GST PDF generation failed"); }
  };

  const downloadGstExcel = async () => {
    try {
      toast.info("Generating GST Excel...");
      const r = await axios.get(`${API}/pharmacy/gst-report/excel`, { params: { date_from: gstDateFrom, date_to: gstDateTo }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a"); a.href = url; a.download = `gst_report_${gstDateFrom}_${gstDateTo}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("GST Excel downloaded");
    } catch { toast.error("GST Excel generation failed"); }
  };

  const clearFilters = () => { setFilters(EMPTY_FILTERS); setPage(1); };
  const hasActiveFilters = Object.values(filters).some(v => v);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200 px-6 py-3" data-testid="pharmacy-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-to-dashboard"><ArrowLeft className="w-4 h-4" /></Button>
            <div>
              <h1 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Pill className="w-5 h-5 text-teal-600" /> Pharmacy Report</h1>
              <p className="text-xs text-slate-400">{formatDateIST(dateFrom)} to {formatDateIST(dateTo)} | {orders.total} {detailedView ? "items" : "orders"}{detailedView ? " (Detailed View)" : ""}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant={detailedView ? "default" : "outline"}
              onClick={() => { setDetailedView(v => !v); setPage(1); }}
              data-testid="toggle-detailed-view"
              className={detailedView ? "bg-indigo-600 hover:bg-indigo-700 text-white" : "border-indigo-300 text-indigo-700 hover:bg-indigo-50"}>
              <List className="w-3.5 h-3.5 mr-1" /> {detailedView ? "Detailed Bill ON" : "Detailed Bill"}
            </Button>
            <div className="w-px h-6 bg-slate-200" />
            <Button size="sm" variant="outline" onClick={() => navigate("/pharmacy/rack-bin")} data-testid="rack-bin-btn" className="border-orange-300 text-orange-700 hover:bg-orange-50">
              <MapPin className="w-3.5 h-3.5 mr-1" /> Rack & Bin
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowPatientBill(true)} data-testid="patient-bill-btn" className="border-violet-300 text-violet-700 hover:bg-violet-50">
              <FileText className="w-3.5 h-3.5 mr-1" /> Patient Bill
            </Button>
            <Button size="sm" variant={showScheduleReport ? "default" : "outline"}
              onClick={toggleScheduleReport}
              data-testid="schedule-report-btn"
              className={showScheduleReport ? "bg-violet-600 hover:bg-violet-700 text-white" : "border-violet-300 text-violet-700 hover:bg-violet-50"}>
              <Pill className="w-3.5 h-3.5 mr-1" /> {showScheduleReport ? "Schedule ON" : "Schedule Report"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setShowGstReport(true); setGstData(null); }} data-testid="gst-report-btn" className="border-blue-300 text-blue-700 hover:bg-blue-50">
              <ReceiptText className="w-3.5 h-3.5 mr-1" /> GST Report
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleExport("csv")} data-testid="export-csv"><Download className="w-3.5 h-3.5 mr-1" /> CSV</Button>
            <div className="relative">
              <Button size="sm" variant="outline" onClick={() => setShowPdfMenu(!showPdfMenu)} data-testid="export-pdf-btn" className="border-teal-300 text-teal-700 hover:bg-teal-50">
                <FileText className="w-3.5 h-3.5 mr-1" /> PDF <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
              {showPdfMenu && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-slate-200 z-50 py-1" data-testid="pdf-menu">
                  <button className="w-full text-left px-3 py-2 text-xs hover:bg-teal-50 flex items-center gap-2" data-testid="pdf-summary" onClick={() => { handlePdfExport("summary"); setShowPdfMenu(false); }}>
                    <PieIcon className="w-3.5 h-3.5 text-teal-600" /> Summary Report
                  </button>
                  <button className="w-full text-left px-3 py-2 text-xs hover:bg-teal-50 flex items-center gap-2" data-testid="pdf-details" onClick={() => { handlePdfExport("details"); setShowPdfMenu(false); }}>
                    <BarChart3 className="w-3.5 h-3.5 text-teal-600" /> Detailed Report
                  </button>
                  <div className="border-t border-slate-100 my-1" />
                  <button className="w-full text-left px-3 py-2 text-xs hover:bg-teal-50 flex items-center gap-2 font-semibold" data-testid="pdf-combined" onClick={() => { handlePdfExport("combined"); setShowPdfMenu(false); }}>
                    <FileText className="w-3.5 h-3.5 text-teal-600" /> Combined Report
                  </button>
                </div>
              )}
            </div>
            <Button size="sm" variant="ghost" onClick={fetchData} data-testid="refresh-pharmacy"><RefreshCw className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 py-4 space-y-5">
        {/* Date Presets */}
        <div className="flex flex-wrap gap-1.5" data-testid="date-presets">
          {DATE_PRESETS.map(p => (
            <button key={p.label} onClick={() => handlePreset(p)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${datePreset === p.label ? "bg-teal-600 text-white shadow-sm" : "bg-white text-slate-600 border border-slate-200 hover:border-teal-300"}`}
              data-testid={`preset-${p.label.toLowerCase().replace(/\s/g, "-")}`}>{p.label}</button>
          ))}
        </div>

        {/* Filters Bar */}
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="filters-panel">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex gap-2">
              <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setDatePreset(""); setPage(1); }} className="w-36 text-xs" data-testid="filter-date-from" />
              <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setDatePreset(""); setPage(1); }} className="w-36 text-xs" data-testid="filter-date-to" />
            </div>
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400" />
              <Input placeholder="Search order, patient, MRN..." value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} className="pl-8 text-xs" data-testid="global-search" />
            </div>
            <Button size="sm" variant="outline" onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters"
              className={hasActiveFilters ? "border-teal-400 text-teal-700" : ""}>
              <Filter className="w-3.5 h-3.5 mr-1" /> Filters {hasActiveFilters && <span className="ml-1 w-4 h-4 rounded-full bg-teal-600 text-white text-[10px] flex items-center justify-center">{Object.values(filters).filter(v => v).length}</span>}
              {showFilters ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
            </Button>
            {hasActiveFilters && <Button size="sm" variant="ghost" onClick={clearFilters} className="text-red-500 text-xs" data-testid="clear-filters"><X className="w-3 h-3 mr-1" /> Clear</Button>}
          </div>
          {showFilters && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mt-4 pt-4 border-t border-slate-100" data-testid="advanced-filters">
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase mb-1 block">Patient Type</label>
                <select value={filters.patient_types} onChange={e => setFilters(f => ({ ...f, patient_types: e.target.value }))} className="w-full rounded-md border border-slate-200 text-xs p-1.5" data-testid="filter-patient-type">
                  <option value="">All</option>
                  {filterOptions.patient_types.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase mb-1 block">Status</label>
                <select value={filters.statuses} onChange={e => setFilters(f => ({ ...f, statuses: e.target.value }))} className="w-full rounded-md border border-slate-200 text-xs p-1.5" data-testid="filter-status">
                  <option value="">All</option>
                  {filterOptions.statuses.map(s => <option key={s} value={s}>{STATUS_CONFIG[s]?.label || s} ({s})</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase mb-1 block">Doctor</label>
                <select value={filters.doctor} onChange={e => setFilters(f => ({ ...f, doctor: e.target.value }))} className="w-full rounded-md border border-slate-200 text-xs p-1.5" data-testid="filter-doctor">
                  <option value="">All</option>
                  {filterOptions.doctors.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase mb-1 block">Scheme</label>
                <select value={filters.scheme} onChange={e => setFilters(f => ({ ...f, scheme: e.target.value }))} className="w-full rounded-md border border-slate-200 text-xs p-1.5" data-testid="filter-scheme">
                  <option value="">All</option>
                  {filterOptions.schemes.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase mb-1 block">Payment Mode</label>
                <select value={filters.payment_mode} onChange={e => setFilters(f => ({ ...f, payment_mode: e.target.value }))} className="w-full rounded-md border border-slate-200 text-xs p-1.5" data-testid="filter-payment-mode">
                  <option value="">All</option>
                  {(() => {
                    const merged = {};
                    (filterOptions.payment_modes || []).forEach(m => { const label = formatMode(m); if (!merged[label]) merged[label] = []; merged[label].push(m); });
                    return Object.entries(merged).map(([label, modes]) => <option key={label} value={modes.join(",")}>{label}</option>);
                  })()}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase mb-1 block">Medicine Name</label>
                <Input placeholder="Search medicine..." value={filters.medicine_name} onChange={e => setFilters(f => ({ ...f, medicine_name: e.target.value }))} className="text-xs" data-testid="filter-medicine-name" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase mb-1 block">MRN</label>
                <Input placeholder="Search MRN" value={filters.mrn} onChange={e => setFilters(f => ({ ...f, mrn: e.target.value }))} className="text-xs" data-testid="filter-mrn" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase mb-1 block">Created By</label>
                <select value={filters.created_by} onChange={e => setFilters(f => ({ ...f, created_by: e.target.value }))} className="w-full rounded-md border border-slate-200 text-xs p-1.5" data-testid="filter-created-by">
                  <option value="">All</option>
                  {filterOptions.creators.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Main Content - Schedule Report OR normal pharmacy view */}
        {showScheduleReport ? (
          <ScheduleReport dateFrom={dateFrom} dateTo={dateTo} />
        ) : (
          <>
            {/* KPI Cards */}
            <PharmacyKPIs summary={summary} />

            {/* Charts */}
            <PharmacyCharts charts={charts} chartView={chartView} setChartView={setChartView} />

            {/* Orders Table - Standard or Detailed */}
            {detailedView ? (
              <PharmacyDetailedTable orders={orders} page={page} setPage={setPage} loading={loading} />
            ) : (
              <PharmacyOrdersTable orders={orders} page={page} setPage={setPage} sortBy={sortBy} handleSort={handleSort} loading={loading} summary={summary} openDrillDown={openDrillDown} handleCreditBill={handleCreditBill} />
            )}
          </>
        )}
      </main>

      {/* Drill-Down Modal */}
      <PharmacyDrillDown drillOrder={drillOrder} onClose={() => setDrillOrder(null)} handleCreditBill={handleCreditBill} />

      {/* Patient Bill Modal */}
      <PatientBillModal show={showPatientBill} onClose={() => setShowPatientBill(false)} form={patientBillForm} setForm={setPatientBillForm} onGenerate={handlePatientBill} loading={patientBillLoading} />

      {/* GST Report Modal */}
      <GstReportModal show={showGstReport} onClose={() => setShowGstReport(false)}
        gstDateFrom={gstDateFrom} setGstDateFrom={setGstDateFrom} gstDateTo={gstDateTo} setGstDateTo={setGstDateTo}
        fetchGstData={fetchGstData} gstLoading={gstLoading} gstData={gstData}
        downloadGstPdf={downloadGstPdf} downloadGstExcel={downloadGstExcel} />
    </div>
  );
}
